import React, {useContext, useEffect, useState} from 'react';
import './EditSelectedProjectsModal.scss';
import CloseIcon from '@mui/icons-material/Close';
import {I18nContext} from '../../contexts/i18nContext';
import {AdminContext} from '../../contexts/adminContext';
import {ProjectsContext} from '../../contexts/projectsContext';
import {Button, CheckboxInput, SelectInput} from '../_common';

const EditSelectedProjectsModal = (
  { closeModal, selectedProjects, refreshProjectsList, clearProjectsSelection, stoen }
) => {
  const { t } = useContext(I18nContext);
  const { getUsersForDropdowns, usersForDropdowns } = useContext(AdminContext);
  const {
    pending,
    addProjectPending,
    projectStatePending,
    updatePgeProjectState,
    updateStoenProjectState,
    updatePgeProject,
    updateStoenProject,
    getSinglePgeProject,
    getSingleStoenProject
  } = useContext(ProjectsContext);
  const [values, setValues] = useState({
    personResponsibleForProjectId: '',
    personResponsibleForImplementationId: '',
    designers: [],
    state: '',
    toBePlanned: false
  });
  const [
    pendingPersonResponsibleForProjectChange,
    setPendingPersonResponsibleForProjectChange
  ] = useState(false);
  const [
    pendingPersonResponsibleForImplementationChange,
    setPendingPersonResponsibleForImplementationChange
  ] = useState(false);
  const [pendingToBePlannedChange, setPendingToBePlannedChange] = useState(false);

  useEffect(() => {
    getUsersForDropdowns();
  }, []);

  const usersOptions = usersForDropdowns.map(user => ({id: user.id, label: user.firstName + " " + user.lastName}));
  const projectStateOptions = stoen
    ? [
      {id: "project", label: t('project')},
      {id: "canceled", label: t('canceled')},
      {id: "completed", label: t('finished')}
    ]
    : [
      {id: "project", label: t('project')},
      {id: "implementation", label: t('implementation')},
      {id: "canceled", label: t('canceled')},
      {id: "completed", label: t('finished')}
    ];

  const updateProjects = async () => {
    if (values.designers.length || values.personResponsibleForProjectId) {
      await changePersonResponsibleForProject ();
    }

    if (values.personResponsibleForImplementationId) {
      await changePersonResponsibleForImplementation();
    }

    if (values.state) {
      await changeState();
    }

    if (values.toBePlanned) {
      await changeToBePlanned();
    }

    refreshProjectsList();
    clearProjectsSelection();
    closeModal();
  };

  const changePersonResponsibleForProject = async () => {
    setPendingPersonResponsibleForProjectChange(true);
    for (const project of selectedProjects) {
      const projectObj = stoen ? await getSingleStoenProject(project) : await getSinglePgeProject(project);
      if (stoen) {
        await updateStoenProject(
          {designers: values.designers},
          project,
          projectObj?.version,
          true
        );
      } else {
        await updatePgeProject(
          {personResponsibleForProjectId: values.personResponsibleForProjectId},
          project,
          projectObj?.version,
          true
        );
      }
    }
    setPendingPersonResponsibleForProjectChange(false);
  };

  const changePersonResponsibleForImplementation = async () => {
    setPendingPersonResponsibleForImplementationChange(true);
    for (const project of selectedProjects) {
      const projectObj = await getSinglePgeProject(project);
      await updatePgeProject(
        {personResponsibleForImplementationId: values.personResponsibleForImplementationId},
        project,
        projectObj?.version,
        true
      );
    }
    setPendingPersonResponsibleForImplementationChange(false);
  };

  const changeState = async () => {
    for (const project of selectedProjects) {
      if (stoen) {
        await updateStoenProjectState(
          values.state,
          project,
          t('state changed using multiple projects edition')
        );
      } else {
        await updatePgeProjectState(
          values.state,
          project,
          t('state changed using multiple projects edition')
        );
      }
    }
  };

  const changeToBePlanned = async () => {
    setPendingToBePlannedChange(true);
    for (const project of selectedProjects) {
      const projectObj = stoen ? await getSingleStoenProject(project) : await getSinglePgeProject(project);
      if (stoen) {
        await updateStoenProject(
          {toBePlanned: true},
          project,
          projectObj?.version,
          true
        );
      } else {
        await updatePgeProject(
          {toBePlanned: true},
          project,
          projectObj?.version,
          true
        );
      }
    }
    setPendingToBePlannedChange(false);
  };

  const changeValue = (name, value, multiple) => {
    let newValues = { ...values };
    if (multiple) {
      if (newValues[name].includes(value)) {
        newValues[name] = newValues[name].filter(val => val !== value);
      } else {
        newValues[name].push(value)
      }
    } else {
      newValues[name] = value;
    }
    setValues(newValues);
    return newValues;
  };

  return (
    <div className='editSelectedProjectsModalBackgroundBlur'>
      <div className='modalBox'>
        <div className='closeButton' onClick={closeModal}>
          <CloseIcon />
        </div>
        <div className='modalTitle'>
          {t('edit selected projects')}
        </div>
        <div className='modalContent'>
          <div className='actionItem'>
            <SelectInput
              label={stoen ? t('designer') : t('person responsible for the project')}
              name={stoen ? 'designers' : 'personResponsibleForProjectId'}
              options={usersOptions}
              onSelect={changeValue}
              value={stoen ? values.designers : values.personResponsibleForProjectId}
              placeholder={t('select')}
              multiple={!!stoen}
              noMargin
            />
          </div>
          {!stoen && (
            <div className='actionItem'>
              <SelectInput
                label={t('person responsible for the implementation')}
                name='personResponsibleForImplementationId'
                options={usersOptions}
                onSelect={changeValue}
                value={values.personResponsibleForImplementationId}
                placeholder={t('select')}
                noMargin
              />
            </div>
          )}
          <div className='actionItem'>
            <SelectInput
              label={t('project state')}
              name='state'
              options={projectStateOptions}
              onSelect={changeValue}
              value={values.state}
              placeholder={t('select')}
              noMargin
            />
          </div>
          {!stoen && (
            <div className='actionItem'>
              <CheckboxInput
                label={t('add to planning')}
                name='toBePlanned'
                value={values.toBePlanned}
                onChange={changeValue}
                noMargin
              />
            </div>
          )}
        </div>
        <div className='modalButtons'>
          <Button
            type='secondary'
            action={closeModal}
            label={t('cancel')}
            disabled={
              pending ||
              addProjectPending ||
              projectStatePending ||
              pendingPersonResponsibleForProjectChange ||
              pendingPersonResponsibleForImplementationChange ||
              pendingToBePlannedChange
            }
          />
          <Button
            type='primary'
            action={updateProjects}
            label={t('save')}
            margin='left'
            pending={
              pending ||
              addProjectPending ||
              projectStatePending ||
              pendingPersonResponsibleForProjectChange ||
              pendingPersonResponsibleForImplementationChange ||
              pendingToBePlannedChange
            }
            disabled={
              pending ||
              addProjectPending ||
              projectStatePending ||
              pendingPersonResponsibleForProjectChange ||
              pendingPersonResponsibleForImplementationChange ||
              pendingToBePlannedChange ||
              (
                !values.state &&
                !values.personResponsibleForImplementationId &&
                !values.personResponsibleForProjectId &&
                !values.designers?.length &&
                !values.toBePlanned
              )
            }
          />
        </div>
      </div>
    </div>
  );
};

export default EditSelectedProjectsModal;
